import { useState, useEffect, useMemo, useRef } from 'react';
import {
  X,
  ChevronLeft,
  ChevronRight,
  RotateCcw,
  Check,
  XIcon,
  Award,
} from 'lucide-react';
import { useFlashcardsInDeck } from '../../hooks/useFlashcards';
import type { FlashcardDto } from '../../api/flashcardsService';
import { useTranslation } from '../../i18n/useTranslation';

interface FlashcardStudyModeProps {
  deckId: string;
  deckName: string;
  /** When set, only cards whose `locationLabel` matches are included. */
  chapterFilter?: string | null;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Payload parsing — picked library cards may embed Cloze / MCQ shapes
// inside the plain question / answer fields. Q/A cards fall through unchanged.
// ---------------------------------------------------------------------------

type StudyPayload =
  | { type: 'QA'; question: string; answer: string }
  | { type: 'Cloze'; rendered: string; answer: string }
  | { type: 'MCQ'; question: string; correct: string; distractors: string[] };

function parsePayload(card: { question: string; answer: string }): StudyPayload {
  // Try MCQ first: answer is JSON with correct + distractors
  try {
    const j = JSON.parse(card.answer);
    if (
      j &&
      typeof j === 'object' &&
      typeof j.correct === 'string' &&
      Array.isArray(j.distractors)
    ) {
      return {
        type: 'MCQ',
        question: card.question,
        correct: j.correct,
        distractors: j.distractors as string[],
      };
    }
  } catch {
    /* not JSON */
  }

  // Cloze: question contains {{c1::...}} markers
  if (/\{\{c\d+::[^}]+\}\}/.test(card.question)) {
    const rendered = card.question.replace(/\{\{c\d+::([^}]+)\}\}/g, '_____');
    return { type: 'Cloze', rendered, answer: card.answer };
  }

  return { type: 'QA', question: card.question, answer: card.answer };
}

function shuffle<T>(arr: T[]): T[] {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

export default function FlashcardStudyMode({
  deckId,
  deckName,
  chapterFilter,
  onClose,
}: FlashcardStudyModeProps) {
  const { t } = useTranslation();
  const { flashcards: allFlashcards, recordStudyResult, isRecording } =
    useFlashcardsInDeck(deckId);

  // When a chapter filter is active, narrow the session to matching cards only.
  // `Unspecified` matches cards with no locationLabel.
  const flashcards = useMemo(() => {
    if (!chapterFilter) return allFlashcards;
    return allFlashcards.filter((c) => {
      const label = (c.locationLabel || '').trim() || 'Unspecified';
      return label === chapterFilter;
    });
  }, [allFlashcards, chapterFilter]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [sessionStats, setSessionStats] = useState({
    correct: 0,
    incorrect: 0,
  });
  const [isComplete, setIsComplete] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  const currentCard = flashcards[currentIndex];
  const progress =
    flashcards.length > 0 ? ((currentIndex + 1) / flashcards.length) * 100 : 0;

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  // Parse the current card's payload (Q/A, Cloze, or MCQ). For MCQ we need a stable
  // shuffle keyed on the card id so the option order doesn't change on every render.
  const payload: StudyPayload | null = currentCard
    ? parsePayload(currentCard)
    : null;

  const mcqOptions = useMemo<string[]>(() => {
    if (!payload || payload.type !== 'MCQ') return [];
    return shuffle([payload.correct, ...payload.distractors]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCard?.id]);

  const [mcqSelected, setMcqSelected] = useState<string | null>(null);

  // Reset MCQ selection whenever the card changes
  useEffect(() => {
    setMcqSelected(null);
  }, [currentCard?.id]);

  useEffect(() => {
    // Reset when deck changes
    setCurrentIndex(0);
    setIsFlipped(false);
    setSessionStats({ correct: 0, incorrect: 0 });
    setIsComplete(false);
  }, [deckId]);

  const handleFlip = () => {
    setIsFlipped(!isFlipped);
  };

  const handleMarkCorrect = async () => {
    if (!currentCard) return;

    await recordStudyResult({ id: currentCard.id, isCorrect: true });
    setSessionStats((prev) => ({ ...prev, correct: prev.correct + 1 }));
    moveToNext();
  };

  const handleMarkIncorrect = async () => {
    if (!currentCard) return;

    await recordStudyResult({ id: currentCard.id, isCorrect: false });
    setSessionStats((prev) => ({ ...prev, incorrect: prev.incorrect + 1 }));
    moveToNext();
  };

  // MCQ: lock in the user's choice, grade it, then auto-advance after a short pause
  const handleMcqSelect = async (option: string) => {
    if (!currentCard || !payload || payload.type !== 'MCQ') return;
    if (mcqSelected !== null) return; // already answered
    setMcqSelected(option);
    const isCorrect = option === payload.correct;
    await recordStudyResult({ id: currentCard.id, isCorrect });
    setSessionStats((prev) => ({
      ...prev,
      correct: prev.correct + (isCorrect ? 1 : 0),
      incorrect: prev.incorrect + (isCorrect ? 0 : 1),
    }));
    // Give the user ~700ms to see the green/red highlight before advancing
    window.setTimeout(() => {
      moveToNext();
    }, 700);
  };

  const moveToNext = () => {
    if (currentIndex < flashcards.length - 1) {
      setCurrentIndex((prev) => prev + 1);
      setIsFlipped(false);
    } else {
      setIsComplete(true);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
      setIsFlipped(false);
    }
  };

  const handleRestart = () => {
    setCurrentIndex(0);
    setIsFlipped(false);
    setSessionStats({ correct: 0, incorrect: 0 });
    setIsComplete(false);
  };

  // Touch/Swipe handlers for mobile
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isRightSwipe && currentIndex > 0) {
      handlePrevious();
    }
    if (isLeftSwipe && isFlipped) {
      handleMarkCorrect();
    }
  };

  // Keyboard shortcuts. Flip / grade keys are disabled for MCQ (selection-driven).
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isComplete) return;
      const isMcq = payload?.type === 'MCQ';

      switch (e.key) {
        case ' ':
        case 'Enter':
          if (isMcq) return;
          e.preventDefault();
          handleFlip();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          handlePrevious();
          break;
        case 'ArrowRight':
          if (isMcq) return;
          e.preventDefault();
          if (isFlipped) {
            handleMarkCorrect();
          }
          break;
        case 'ArrowDown':
          if (isMcq) return;
          e.preventDefault();
          if (isFlipped) {
            handleMarkIncorrect();
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFlipped, isComplete, currentIndex, currentCard, payload?.type]);

  if (flashcards.length === 0) {
    return (
      <div className="fixed inset-0 bg-neutral-900 z-[2000] flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl p-6 md:p-8 max-w-md w-full text-center">
          <p className="text-neutral-800 text-lg md:text-xl mb-6">
            {t('App.Reader.Flashcard.StudyEmptyMessage')}
          </p>
          <button
            onClick={onClose}
            className="min-h-[44px] min-w-[44px] px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors text-base font-medium"
            aria-label={t('App.Reader.Flashcard.StudyCloseLabel')}
          >
            {t('App.Reader.Flashcard.StudyCloseLabel')}
          </button>
        </div>
      </div>
    );
  }

  if (isComplete) {
    const totalCards = sessionStats.correct + sessionStats.incorrect;
    const accuracy =
      totalCards > 0 ? (sessionStats.correct / totalCards) * 100 : 0;

    return (
      <div className="fixed inset-0 bg-gradient-to-br from-primary-600 to-primary-800 z-[2000] flex items-center justify-center p-4">
        <div className="bg-white rounded-xl md:rounded-2xl shadow-2xl max-w-md w-full p-6 md:p-8 text-center">
          <div className="mb-6">
            <Award className="w-16 h-16 md:w-20 md:h-20 text-warning-500 mx-auto mb-4" aria-hidden="true" />
            <h2 className="text-2xl md:text-3xl font-bold text-neutral-800 mb-2">
              {t('App.Reader.Flashcard.StudyCompleteTitle')}
            </h2>
            <p className="text-neutral-600 text-base md:text-lg">{deckName}</p>
          </div>

          <div className="grid grid-cols-3 gap-3 md:gap-4 mb-6 md:mb-8">
            <div className="bg-neutral-50 rounded-lg p-3 md:p-4">
              <div className="text-xl md:text-2xl font-bold text-neutral-800">
                {totalCards}
              </div>
              <div className="text-xs md:text-sm text-neutral-600 mt-1">{t('App.Reader.Flashcard.StudyCardsLabel')}</div>
            </div>
            <div className="bg-success-50 rounded-lg p-3 md:p-4">
              <div className="text-xl md:text-2xl font-bold text-success-600">
                {sessionStats.correct}
              </div>
              <div className="text-xs md:text-sm text-neutral-600 mt-1">{t('App.Reader.Flashcard.StudyCorrectLabel')}</div>
            </div>
            <div className="bg-error-50 rounded-lg p-3 md:p-4">
              <div className="text-xl md:text-2xl font-bold text-error-600">
                {sessionStats.incorrect}
              </div>
              <div className="text-xs md:text-sm text-neutral-600 mt-1">{t('App.Reader.Flashcard.StudyIncorrectLabel')}</div>
            </div>
          </div>

          <div className="mb-6 md:mb-8">
            <div className="text-sm md:text-base text-neutral-700 font-medium mb-2">{t('App.Reader.Flashcard.StudyAccuracyLabel')}</div>
            <div className="h-4 bg-neutral-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-success-500 to-success-600 transition-all duration-500"
                style={{ width: `${accuracy}%` }}
                role="progressbar"
                aria-valuenow={accuracy}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={t('App.Reader.Flashcard.StudyProgressAriaLabel', { progress: accuracy.toFixed(0) })}
              />
            </div>
            <div className="text-2xl md:text-3xl font-bold text-neutral-800 mt-2">
              {accuracy.toFixed(0)}%
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleRestart}
              className="flex-1 min-h-[44px] px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors flex items-center justify-center gap-2 text-base font-medium"
              aria-label={t('App.Reader.Flashcard.StudyAgainButton')}
            >
              <RotateCcw className="w-5 h-5" aria-hidden="true" />
              {t('App.Reader.Flashcard.StudyAgainButton')}
            </button>
            <button
              onClick={onClose}
              className="flex-1 min-h-[44px] px-6 py-3 bg-neutral-200 text-neutral-800 rounded-lg hover:bg-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-400 focus:ring-offset-2 transition-colors text-base font-medium"
              aria-label={t('App.Reader.Flashcard.StudyCloseButton')}
            >
              {t('App.Reader.Flashcard.StudyCloseButton')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-primary-600 to-primary-800 z-[2000] flex flex-col">
      {/* Header - Mobile optimized */}
      <div className="bg-white shadow-md flex-shrink-0">
        <div className="max-w-4xl mx-auto px-4 py-3 md:py-4 flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h2 className="text-base md:text-lg font-semibold text-neutral-800 truncate">
              {deckName}
              {chapterFilter && (
                <span className="ml-2 text-xs md:text-sm font-normal text-neutral-500">· {chapterFilter}</span>
              )}
            </h2>
            {currentCard?.locationLabel && !chapterFilter && (
              <p className="text-xs md:text-sm text-neutral-500 truncate mt-0.5">
                {currentCard.locationLabel}
              </p>
            )}
            <div className="flex items-center gap-3 md:gap-4 mt-1">
              <span className="text-sm md:text-base text-neutral-700">
                <span className="font-medium">{currentIndex + 1}</span>
                <span className="text-neutral-500"> / {flashcards.length}</span>
              </span>
              <div className="flex items-center gap-2 text-sm md:text-base" role="status" aria-live="polite">
                <span className="text-success-600 font-medium" aria-label={`${sessionStats.correct} correct answers`}>
                  ✓ {sessionStats.correct}
                </span>
                <span className="text-error-600 font-medium" aria-label={`${sessionStats.incorrect} incorrect answers`}>
                  ✗ {sessionStats.incorrect}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="min-h-[44px] min-w-[44px] p-2 md:p-3 hover:bg-neutral-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors flex-shrink-0"
            title={t('App.Reader.Flashcard.StudyCloseAriaLabel')}
            aria-label={t('App.Reader.Flashcard.StudyCloseAriaLabel')}
          >
            <X className="w-6 h-6 text-neutral-600" aria-hidden="true" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="h-1 bg-neutral-200">
          <div
            className="h-full bg-primary-600 transition-all duration-300"
            style={{ width: `${progress}%` }}
            role="progressbar"
            aria-valuenow={progress}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={t('App.Reader.Flashcard.StudyProgressAriaLabel', { progress: Math.round(progress) })}
          />
        </div>
      </div>

      {/* Card - Responsive sizing */}
      <div className="flex-1 flex items-center justify-center p-4 md:p-6 overflow-y-auto">
        <div className="w-full max-w-3xl">
          {payload?.type === 'MCQ' ? (
            // MCQ: static, content-sized card. Right-swipe still goes back.
            <div
              className="bg-white rounded-xl md:rounded-2xl shadow-2xl p-5 md:p-6 lg:p-8 flex flex-col items-center touch-manipulation"
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
            >
              <div className="text-xs md:text-sm font-semibold text-primary-600 mb-2 md:mb-3 uppercase tracking-wide">
                {t('App.Reader.Flashcard.MCQInstructions')}
              </div>
              <div className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-semibold text-neutral-800 text-center mb-4 md:mb-5 leading-snug px-2">
                {payload.question}
              </div>
              <div className="w-full max-w-xl flex flex-col gap-2">
                {mcqOptions.map((opt) => {
                  const isAnswered = mcqSelected !== null;
                  const isChosen = mcqSelected === opt;
                  const isCorrect = payload.correct === opt;
                  const revealCorrect = isAnswered && isCorrect;
                  const revealWrong = isAnswered && isChosen && !isCorrect;
                  const baseClasses =
                    'min-h-[44px] px-4 py-2.5 text-left rounded-lg border text-base md:text-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2';
                  const stateClasses = revealCorrect
                    ? 'bg-success-100 border-success-500 text-success-800'
                    : revealWrong
                    ? 'bg-error-100 border-error-500 text-error-800'
                    : isAnswered
                    ? 'bg-neutral-100 border-neutral-200 text-neutral-600'
                    : 'bg-white border-neutral-300 text-neutral-800 hover:bg-neutral-50';
                  return (
                    <button
                      key={opt}
                      type="button"
                      disabled={isAnswered || isRecording}
                      onClick={() => handleMcqSelect(opt)}
                      className={`${baseClasses} ${stateClasses}`}
                      aria-pressed={isChosen}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            // QA / Cloze: flip card with fixed 3D height for symmetry.
            <div
              ref={cardRef}
              className="relative w-full touch-manipulation cursor-pointer"
              onClick={handleFlip}
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
              style={{ perspective: '1000px' }}
              role="button"
              tabIndex={0}
              aria-label={
                isFlipped
                  ? t('App.Reader.Flashcard.AnswerCardAriaLabel')
                  : t('App.Reader.Flashcard.QuestionCardAriaLabel')
              }
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleFlip();
                }
              }}
            >
              <div
                className="relative w-full transition-transform duration-300 ease-out"
                style={{
                  transformStyle: 'preserve-3d',
                  transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
                  minHeight: 'clamp(300px, 50vh, 500px)',
                }}
              >
                {/* Front (Question / Cloze) */}
                <div
                  className="absolute inset-0 bg-white rounded-xl md:rounded-2xl shadow-2xl p-6 md:p-8 lg:p-10 flex flex-col items-center justify-center"
                  style={{ backfaceVisibility: 'hidden' }}
                  aria-hidden={isFlipped}
                >
                  <div className="text-xs md:text-sm font-semibold text-primary-600 mb-3 md:mb-4 uppercase tracking-wide">
                    {payload?.type === 'Cloze' ? t('App.Reader.Flashcard.TypeFillInBlank') : t('App.Reader.Flashcard.QuestionLabel')}
                  </div>
                  <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-semibold text-neutral-800 text-center mb-6 md:mb-8 leading-tight px-2">
                    {payload?.type === 'Cloze' ? payload.rendered : payload?.question}
                  </div>
                  <div className="text-sm md:text-base text-neutral-500 mt-6 md:mt-8 text-center px-2">
                    {t('App.Reader.Flashcard.TapToRevealLabel')}
                  </div>
                </div>

                {/* Back (Answer) */}
                <div
                  className="absolute inset-0 bg-gradient-to-br from-primary-600 to-primary-700 rounded-xl md:rounded-2xl shadow-2xl p-6 md:p-8 lg:p-10 flex flex-col items-center justify-center"
                  style={{
                    backfaceVisibility: 'hidden',
                    transform: 'rotateY(180deg)',
                  }}
                  aria-hidden={!isFlipped}
                >
                  <div className="text-xs md:text-sm font-semibold text-white mb-3 md:mb-4 uppercase tracking-wide opacity-90">
                    {t('App.Reader.Flashcard.AnswerLabel')}
                  </div>
                  <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-semibold text-white text-center mb-6 md:mb-8 leading-tight px-2">
                    {payload?.type === 'Cloze' ? (
                      (() => {
                        // Replace the first blank with a highlighted answer; subsequent
                        // blanks remain underscored so multiple-cloze cards still read.
                        const idx = payload.rendered.indexOf('_____');
                        if (idx < 0) {
                          return (
                            <>
                              {payload.rendered}{' '}
                              <span className="underline decoration-yellow-300">
                                {payload.answer}
                              </span>
                            </>
                          );
                        }
                        const before = payload.rendered.slice(0, idx);
                        const after = payload.rendered.slice(idx + '_____'.length);
                        return (
                          <>
                            {before}
                            <span className="px-2 py-0.5 rounded bg-yellow-300 text-neutral-900">
                              {payload.answer}
                            </span>
                            {after}
                          </>
                        );
                      })()
                    ) : (
                      payload?.answer
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Controls — full row for QA/Cloze; just a back arrow for MCQ */}
          {payload?.type === 'MCQ' ? (
            currentIndex > 0 && (
              <div className="mt-4 flex items-center justify-center">
                <button
                  onClick={handlePrevious}
                  className="min-h-[44px] min-w-[44px] p-2.5 bg-white/90 rounded-full shadow-md hover:bg-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-primary-600 transition-colors"
                  title={t('App.Reader.Flashcard.PreviousCardLabel')}
                  aria-label={t('App.Reader.Flashcard.PreviousCardLabel')}
                >
                  <ChevronLeft className="w-5 h-5 md:w-6 md:h-6 text-neutral-800" aria-hidden="true" />
                </button>
              </div>
            )
          ) : (
            <div className="mt-4 md:mt-6 flex items-center justify-center gap-3 md:gap-4">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handlePrevious();
                }}
                disabled={currentIndex === 0}
                className="min-h-[44px] min-w-[44px] p-3 md:p-4 bg-white rounded-full shadow-lg hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-primary-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                title={t('App.Reader.Flashcard.PreviousCardLabel')}
                aria-label={t('App.Reader.Flashcard.PreviousCardLabel')}
              >
                <ChevronLeft className="w-6 h-6 md:w-7 md:h-7 text-neutral-800" aria-hidden="true" />
              </button>

              {isFlipped && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMarkIncorrect();
                    }}
                    disabled={isRecording}
                    className="min-h-[44px] px-5 py-3 md:px-6 md:py-3 bg-error-500 text-white rounded-full shadow-lg hover:bg-error-600 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-primary-600 transition-colors flex items-center gap-2 disabled:opacity-50 text-sm md:text-base font-medium"
                    title={t('App.Reader.Flashcard.MarkIncorrectLabel')}
                    aria-label={t('App.Reader.Flashcard.MarkIncorrectAriaLabel')}
                  >
                    <XIcon className="w-4 h-4 md:w-5 md:h-5" aria-hidden="true" />
                    <span className="hidden sm:inline">{t('App.Reader.Flashcard.IncorrectButtonLabel')}</span>
                    <span className="sm:hidden">{t('App.Reader.Flashcard.IncorrectButtonLabel')}</span>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMarkCorrect();
                    }}
                    disabled={isRecording}
                    className="min-h-[44px] px-5 py-3 md:px-6 md:py-3 bg-success-500 text-white rounded-full shadow-lg hover:bg-success-600 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-primary-600 transition-colors flex items-center gap-2 disabled:opacity-50 text-sm md:text-base font-medium"
                    title={t('App.Reader.Flashcard.MarkCorrectLabel')}
                    aria-label={t('App.Reader.Flashcard.MarkCorrectAriaLabel')}
                  >
                    <Check className="w-4 h-4 md:w-5 md:h-5" aria-hidden="true" />
                    <span className="hidden sm:inline">{t('App.Reader.Flashcard.CorrectButtonLabel')}</span>
                    <span className="sm:hidden">{t('App.Reader.Flashcard.CorrectButtonLabel')}</span>
                  </button>
                </>
              )}

              {!isFlipped && currentIndex < flashcards.length - 1 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentIndex((prev) => prev + 1);
                    setIsFlipped(false);
                  }}
                  className="min-h-[44px] min-w-[44px] p-3 md:p-4 bg-white rounded-full shadow-lg hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-primary-600 transition-colors"
                  title={t('App.Reader.Flashcard.NextCardLabel')}
                  aria-label={t('App.Reader.Flashcard.NextCardLabel')}
                >
                  <ChevronRight className="w-6 h-6 md:w-7 md:h-7 text-neutral-800" aria-hidden="true" />
                </button>
              )}
            </div>
          )}

          {/* Keyboard Shortcuts Help — context-aware */}
          <div className="mt-3 md:mt-4 text-center text-white text-sm md:text-base">
            <p className="bg-black bg-opacity-25 rounded-lg px-4 py-2 inline-block">
              {payload?.type === 'MCQ' ? (
                t('App.Reader.Flashcard.KeyboardShortcutsMCQ')
              ) : (
                t('App.Reader.Flashcard.KeyboardShortcutsQA')
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
